import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { Button, Grid, Stack } from '@mui/material'

import { Analyst, Analysts } from '../../../../../../components/Analyst/Analyst'
import { GameGroup } from '../../../../../../components/GameGroup/GameGroup'
import { HtmlContentParser } from '../../../../../../components/HtmlContentParser/HtmlContentParser'
import languageService from '../../../../../../services/LanguageService'
import { TranslationMap } from '../../../../../../types/TranslationMap'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import type { NewsCardContentSectionProps } from '../NewsCardContentSections'
import './NewsCardContentReportSection.scss'

// Props type definition - extends the base NewsCardContentSectionProps
type NewsCardContentReportSectionProps = NewsCardContentSectionProps

/**
 * NewsCardContentReportSection Component
 *
 * Renders a report-specific content section within a news card. This component displays:
 * - Report description/comment with HTML content support
 * - Associated games with their icons
 * - A "Read More" button to navigate to the full report
 * - Analyst information (single analyst, multiple analysts, or default GameRefinery)
 *
 * The layout uses a 3-column grid structure for optimal information display.
 */
export const NewsCardContentReportSection: FC<NewsCardContentReportSectionProps> = ({ newsEntry }) => {
  // Get current user's language preference for content localization
  const language = useCurrentUserLanguage()
  // Navigation hook for programmatic routing
  const navigate = useNavigate()
  // Translation hook for internationalization
  const { t } = useTranslation()

  return (
    <div className="NewsCardContentReportSection">
      {/* Report Description Section */}
      <div className="NewsCardContentReportSection__report-description">
        {/* Render HTML comment content if available, with language fallback */}
        {newsEntry.feedEntry.entryData.comment && (
          <HtmlContentParser
            rawHtml={
              // Try to get content in user's language first, fallback to default language
              (newsEntry.feedEntry.entryData.comment as TranslationMap)?.[language] ||
              (newsEntry.feedEntry.entryData.comment as TranslationMap)?.[languageService.defaultLanguage]
            }
          />
        )}
      </div>

      {/* Report Metadata Grid - 3-column layout for games, action button, and analysts */}
      <Grid container className="NewsCardContentReportSection__report-metadata" direction={{ xs: 'column', md: 'row' }} rowSpacing={{ xs: 2, md: 0 }}>
        {/* Left Column: Associated Games */}
        <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
          <GameGroup games={newsEntry.feedEntry.entryData.games || []} max={5} componentsProps={{ additionalAvatar: { sx: { width: 40, height: 40 } } }} />
        </Grid>

        {/* Center Column: Read More Button */}
        <Grid item xs={12} sm={10} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button variant="contained" onClick={() => navigate(`/reports/${newsEntry.feedEntry.entryData.reportId}`)} size="small">
            {t('common:read_more')}
          </Button>
        </Grid>

        {/* Right Column: Analyst Information */}
        <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          {/* Display analyst information with different layouts based on count */}
          {newsEntry.feedEntry.entryData.analysts && (
            <Stack direction="row" spacing={0.5} sx={{ justifyContent: { xs: 'center', sm: 'flex-end' } }}>
              {/* Single Analyst Display */}
              {newsEntry.feedEntry.entryData?.analysts && newsEntry.feedEntry.entryData?.analysts.length === 1 && (
                <Analyst
                  name={newsEntry.feedEntry.entryData?.analysts[0].name}
                  picture={newsEntry.feedEntry.entryData?.analysts[0]?.picture}
                  title={newsEntry.feedEntry.entryData?.analysts[0]?.title}
                  size="small"
                  direction="row-reverse"
                />
              )}

              {/* Multiple Analysts Display - shows up to 4 analysts */}
              {newsEntry.feedEntry.entryData?.analysts && newsEntry.feedEntry.entryData?.analysts.length > 1 && (
                <Analysts analysts={newsEntry.feedEntry.entryData?.analysts} max={5} />
              )}

              {/* Default Analyst Display - shown when no analysts are assigned */}
              {(!newsEntry.feedEntry.entryData?.analysts || newsEntry.feedEntry.entryData.analysts.length === 0) && (
                <Analyst name="GameRefinery" title="Report" direction="row-reverse" icon={<SupportAgentIcon />} mr={2} />
              )}
            </Stack>
          )}
        </Grid>
      </Grid>
    </div>
  )
}
